import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Container, Stepper, Step, StepLabel, Button, Typography, Grid, TextField,
  FormControl, InputLabel, Select, MenuItem, Card, CardContent, CardHeader,
  useMediaQuery, useTheme, Alert, Stack, InputAdornment, Avatar, Divider,
  Checkbox, FormControlLabel
} from '@mui/material';

import HomeWorkIcon from '@mui/icons-material/HomeWork';
import CategoryIcon from '@mui/icons-material/Category';
import DescriptionIcon from '@mui/icons-material/Description';
import PersonIcon from '@mui/icons-material/Person';
import ReviewsIcon from '@mui/icons-material/Reviews';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ApartmentIcon from '@mui/icons-material/Apartment';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import MarkunreadMailboxIcon from '@mui/icons-material/MarkunreadMailbox';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import ScheduleIcon from '@mui/icons-material/Schedule';
import BoltIcon from '@mui/icons-material/Bolt';
import PlumbingIcon from '@mui/icons-material/Plumbing';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import ElectricalServicesIcon from '@mui/icons-material/ElectricalServices';
import RoofingIcon from '@mui/icons-material/Roofing';
import LockIcon from '@mui/icons-material/Lock';
import KitchenIcon from '@mui/icons-material/Kitchen';
import BuildIcon from '@mui/icons-material/Build';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';

// CATEGORY ICONS
const CATEGORY_CONFIG = [
  { key: 'Plumbing', icon: <PlumbingIcon /> },
  { key: 'Heating', icon: <WhatshotIcon /> },
  { key: 'Electrical', icon: <ElectricalServicesIcon /> },
  { key: 'Roofing', icon: <RoofingIcon /> },
  { key: 'Lock / Security', icon: <LockIcon /> },
  { key: 'Appliance', icon: <KitchenIcon /> },
  { key: 'Other', icon: <BuildIcon /> },
];

const URGENCY = ['Emergency (within 4h)', 'Soon (24–48h)', 'Flexible (this week)'];
const SLOTS = ['Morning (8–12)', 'Afternoon (12–4)', 'Evening (4–8)'];

// Helpers
const isEmail = (v) => /\S+@\S+\.\S+/.test((v ?? '').trim());
const isPhone = (v) => /^\+?[0-9 ()-]{7,20}$/.test((v ?? '').trim());
const nonEmpty = (v) => (v ?? '').toString().trim().length > 0;
const clampLen = (v, n) => (v ?? '').slice(0, n);

// Step Icon
const StepBubble = ({ icon, active, completed }) => (
  <Box sx={{
    width: 36, height: 36, borderRadius: '50%', display: 'grid', placeItems: 'center',
    color: completed ? 'common.white' : active ? 'primary.main' : 'text.secondary',
    bgcolor: completed ? 'primary.main' : active ? 'primary.light' : 'action.hover',
    boxShadow: active ? 4 : 0, transition: 'all .2s ease',
  }}>
    {icon}
  </Box>
);

function StepIconComponent(props) {
  const { active, completed, icon } = props;
  const map = { 1: <HomeWorkIcon />, 2: <CategoryIcon />, 3: <DescriptionIcon />, 4: <PersonIcon />, 5: <ReviewsIcon /> };
  return <StepBubble icon={map[icon]} active={active} completed={completed} />;
}

// ──────────────────────────
// STEP COMPONENTS
// ──────────────────────────

// 1) LOCATION (typable)
const PropertyStep = ({ data, onChange, errors }) => (
  <Grid container spacing={3} sx={{ mt: 1 }}>
    <Grid item xs={12}>
      <TextField
        fullWidth required autoFocus
        label="Address / Location"
        placeholder="e.g., 12 Palm Grove, 1st Floor, Colombo 03"
        value={data.address}
        onChange={(e) => onChange('address', clampLen(e.target.value, 120))}
        error={!!errors.address}
        helperText={errors.address || `${(data.address || '').length}/120`}
        InputProps={{ startAdornment: <InputAdornment position="start"><LocationOnIcon /></InputAdornment> }}
        inputProps={{ autoComplete: 'off', 'aria-label': 'address' }}
      />
    </Grid>
    <Grid item xs={12} sm={6}>
      <TextField
        fullWidth required
        label="City / Town"
        placeholder="e.g., Colombo"
        value={data.city}
        onChange={(e) => onChange('city', clampLen(e.target.value, 40))}
        error={!!errors.city}
        helperText={errors.city || `${(data.city || '').length}/40`}
        InputProps={{ startAdornment: <InputAdornment position="start"><LocationCityIcon /></InputAdornment> }}
      />
    </Grid>
    <Grid item xs={12} sm={6}>
      <TextField
        fullWidth required
        label="Postcode / ZIP"
        placeholder="e.g., 00300"
        value={data.postcode}
        onChange={(e) => onChange('postcode', clampLen(e.target.value.toUpperCase(), 10))}
        error={!!errors.postcode}
        helperText={errors.postcode || `${(data.postcode || '').length}/10`}
        InputProps={{ startAdornment: <InputAdornment position="start"><MarkunreadMailboxIcon /></InputAdornment> }}
      />
    </Grid>
    <Grid item xs={12}>
      <TextField
        fullWidth
        label="Property Reference (optional)"
        placeholder="Flat/Unit, Landmark, Gate code, etc."
        value={data.ref || ''}
        onChange={(e) => onChange('ref', clampLen(e.target.value, 80))}
        helperText={`${(data.ref || '').length}/80`}
        InputProps={{ startAdornment: <InputAdornment position="start"><ApartmentIcon /></InputAdornment> }}
      />
    </Grid>
    <Grid item xs={12}>
      <Alert severity="info">
        Type any location details freely. Landmarks and access notes help engineers find you faster.
      </Alert>
    </Grid>
  </Grid>
);

// 2) CATEGORY
const CategoryStep = ({ data, onChange, errors }) => (
  <Box sx={{ mt: 1 }}>
    <Grid container spacing={2}>
      {CATEGORY_CONFIG.map((c) => {
        const active = data.category === c.key;
        return (
          <Grid item xs={6} sm={4} md={3} key={c.key}>
            <Card
              onClick={() => onChange('category', c.key)}
              elevation={active ? 6 : 1}
              sx={{
                p: 2, cursor: 'pointer', borderRadius: 2, textAlign: 'center',
                border: '1px solid', borderColor: active ? 'primary.main' : 'divider',
                transition: 'transform .15s', '&:hover': { transform: 'translateY(-2px)' }
              }}
            >
              <Avatar sx={{ mx: 'auto', mb: 1, bgcolor: active ? 'primary.main' : 'action.hover', color: active ? 'common.white' : 'text.secondary' }}>
                {c.icon}
              </Avatar>
              <Typography variant="body2" fontWeight={600}>{c.key}</Typography>
            </Card>
          </Grid>
        );
      })}
    </Grid>
    <FormControl fullWidth margin="normal" sx={{ mt: 3 }}>
      <InputLabel id="category-label">Select a Repair Category</InputLabel>
      <Select
        labelId="category-label"
        id="category"
        value={data.category}
        label="Select a Repair Category"
        onChange={(e) => onChange('category', e.target.value)}
        error={!!errors.category}
      >
        <MenuItem value="" disabled>Choose a category</MenuItem>
        {CATEGORY_CONFIG.map((c) => (
          <MenuItem key={c.key} value={c.key}>{c.key}</MenuItem>
        ))}
      </Select>
      {errors.category && <Typography variant="caption" color="error">{errors.category}</Typography>}
    </FormControl>
  </Box>
);

// 3) DESCRIPTION (+ urgency, slot, photos)
const DescriptionStep = ({ data, onChange, onPhotosChange, errors }) => (
  <Grid container spacing={3} sx={{ mt: 1 }}>
    <Grid item xs={12}>
      <TextField
        fullWidth required
        label="Issue Title"
        placeholder="e.g., Leaky faucet in the kitchen"
        value={data.title}
        onChange={(e) => onChange('title', clampLen(e.target.value, 80))}
        error={!!errors.title}
        helperText={errors.title || `${(data.title || '').length}/80`}
      />
    </Grid>
    <Grid item xs={12}>
      <TextField
        fullWidth required
        label="Detailed Description"
        placeholder="Describe the problem, when it started, any noises/smells, and if power/water is affected."
        value={data.description}
        onChange={(e) => onChange('description', clampLen(e.target.value, 600))}
        multiline minRows={4}
        error={!!errors.description}
        helperText={errors.description || `${(data.description || '').length}/600`}
      />
    </Grid>
    <Grid item xs={12} sm={6}>
      <FormControl fullWidth>
        <InputLabel id="urgency-label">Urgency</InputLabel>
        <Select
          labelId="urgency-label"
          value={data.urgency || ''}
          label="Urgency"
          onChange={(e) => onChange('urgency', e.target.value)}
        >
          {URGENCY.map((u) => (
            <MenuItem key={u} value={u}>
              <Stack direction="row" spacing={1} alignItems="center"><BoltIcon fontSize="small" /><span>{u}</span></Stack>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Grid>
    <Grid item xs={12} sm={6}>
      <FormControl fullWidth>
        <InputLabel id="slot-label">Preferred Time</InputLabel>
        <Select
          labelId="slot-label"
          value={data.slot || ''}
          label="Preferred Time"
          onChange={(e) => onChange('slot', e.target.value)}
        >
          {SLOTS.map((s) => (
            <MenuItem key={s} value={s}>
              <Stack direction="row" spacing={1} alignItems="center"><ScheduleIcon fontSize="small" /><span>{s}</span></Stack>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Grid>
    <Grid item xs={12}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>Add photos (up to 3)</Typography>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ flexWrap: 'wrap' }}>
        {Array.from(data.photos || []).map((src, idx) => (
          <Box key={idx} sx={{ width: 88, height: 88, borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
            <img alt={`issue-${idx}`} src={src} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </Box>
        ))}
        <Button variant="outlined" startIcon={<CameraAltIcon />} component="label" sx={{ height: 40 }}>
          Upload
          <input
            hidden accept="image/*" multiple type="file"
            onChange={(e) => {
              const files = Array.from(e.target.files || []).slice(0, 3);
              const readers = files.map((f) => new Promise((res) => {
                const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(f);
              }));
              Promise.all(readers).then((urls) => onPhotosChange(urls));
            }}
          />
        </Button>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        Clear, close-up photos help us assign the right engineer faster.
      </Typography>
    </Grid>
  </Grid>
);

// 4) CONTACT
const DetailsStep = ({ data, onChange, errors }) => (
  <Grid container spacing={3} sx={{ mt: 1 }}>
    <Grid item xs={12}>
      <TextField
        fullWidth required
        label="Your Name"
        placeholder="e.g., John Doe"
        value={data.name}
        onChange={(e) => onChange('name', clampLen(e.target.value, 60))}
        error={!!errors.name}
        helperText={errors.name || `${(data.name || '').length}/60`}
        InputProps={{ startAdornment: <InputAdornment position="start"><PersonIcon /></InputAdornment> }}
      />
    </Grid>
    <Grid item xs={12} sm={6}>
      <TextField
        fullWidth required type="email"
        label="Email Address"
        placeholder="name@example.com"
        value={data.email}
        onChange={(e) => onChange('email', clampLen(e.target.value, 100))}
        error={!!errors.email}
        helperText={errors.email || `${(data.email || '').length}/100`}
        InputProps={{ startAdornment: <InputAdornment position="start"><EmailIcon /></InputAdornment> }}
      />
    </Grid>
    <Grid item xs={12} sm={6}>
      <TextField
        fullWidth required type="tel"
        label="Phone Number"
        placeholder="+94 71 234 5678"
        value={data.phone}
        onChange={(e) => onChange('phone', clampLen(e.target.value, 20))}
        error={!!errors.phone}
        helperText={errors.phone || 'Include country code if possible'}
        InputProps={{ startAdornment: <InputAdornment position="start"><PhoneIphoneIcon /></InputAdornment> }}
      />
    </Grid>
    <Grid item xs={12}>
      <FormControlLabel
        control={<Checkbox checked={!!data.consent} onChange={(e) => onChange('consent', e.target.checked)} />}
        label="I consent to be contacted about this request."
      />
      {errors.consent && <Typography variant="caption" color="error" sx={{ display: 'block' }}>{errors.consent}</Typography>}
    </Grid>
  </Grid>
);

// 5) REVIEW
const ReviewStep = ({ data }) => (
  <Box sx={{ mt: 2 }}>
    <Card variant="outlined" sx={{ bgcolor: 'grey.50' }}>
      <CardHeader title="Review Your Request" subheader="Please check the details below before submitting." />
      <CardContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
            <CheckCircleOutlineIcon color="success" sx={{ mr: 1 }} /> Location
          </Typography>
          <Typography variant="body1" sx={{ ml: 4 }}>{data.address}</Typography>
          <Typography variant="body2" sx={{ ml: 4, color: 'text.secondary' }}>
            {data.city} {data.postcode && `• ${data.postcode}`}
          </Typography>
          {data.ref && <Typography variant="body2" sx={{ ml: 4, color: 'text.secondary' }}>Ref: {data.ref}</Typography>}
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
            <CheckCircleOutlineIcon color="success" sx={{ mr: 1 }} /> Category
          </Typography>
          <Typography variant="body1" sx={{ ml: 4 }}>{data.category}</Typography>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
            <CheckCircleOutlineIcon color="success" sx={{ mr: 1 }} /> Issue
          </Typography>
          <Typography variant="body1" sx={{ ml: 4, fontWeight: 'medium' }}>{data.title}</Typography>
          <Typography variant="body2" sx={{ ml: 4, color: 'grey.600' }}>{data.description}</Typography>
        </Box>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6}><Typography variant="body2"><strong>Urgency:</strong> {data.urgency || '—'}</Typography></Grid>
          <Grid item xs={12} sm={6}><Typography variant="body2"><strong>Preferred Time:</strong> {data.slot || '—'}</Typography></Grid>
        </Grid>

        {(data.photos || []).length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
              <CheckCircleOutlineIcon color="success" sx={{ mr: 1 }} /> Photos
            </Typography>
            <Stack direction="row" spacing={1} sx={{ ml: 4, mt: 1, flexWrap: 'wrap' }}>
              {data.photos.map((src, i) => (
                <Box key={i} sx={{ width: 88, height: 88, borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
                  <img alt={`review-${i}`} src={src} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </Box>
              ))}
            </Stack>
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        <Box>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
            <CheckCircleOutlineIcon color="success" sx={{ mr: 1 }} /> Your Details
          </Typography>
          <Typography variant="body1" sx={{ ml: 4 }}><strong>Name:</strong> {data.name}</Typography>
          <Typography variant="body1" sx={{ ml: 4 }}><strong>Email:</strong> {data.email}</Typography>
          <Typography variant="body1" sx={{ ml: 4 }}><strong>Phone:</strong> {data.phone}</Typography>
        </Box>
      </CardContent>
    </Card>
  </Box>
);

// ──────────────────────────
// MAIN COMPONENT
// ──────────────────────────
function App() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [activeStep, setActiveStep] = useState(0);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const [data, setData] = useState({
    // location
    address: '', city: '', postcode: '', ref: '',
    // category/issue
    category: '', title: '', description: '', urgency: '', slot: '', photos: [],
    // contact
    name: '', email: '', phone: '', consent: false,
  });

  const steps = useMemo(() => [
    { title: 'Location', component: PropertyStep },
    { title: 'Category', component: CategoryStep },
    { title: 'Describe Issue', component: DescriptionStep },
    { title: 'Your Details', component: DetailsStep },
    { title: 'Review & Submit', component: ReviewStep },
  ], []);

  const Current = steps[activeStep].component;

  // PURE error collector (NO state updates here)
  const collectErrors = (stepIndex) => {
    const e = {};
    if (stepIndex === 0) {
      if (!nonEmpty(data.address)) e.address = 'Address/location is required';
      if (!nonEmpty(data.city)) e.city = 'City/Town is required';
      if (!nonEmpty(data.postcode)) e.postcode = 'Postcode/ZIP is required';
    }
    if (stepIndex === 1) {
      if (!nonEmpty(data.category)) e.category = 'Please choose a category';
    }
    if (stepIndex === 2) {
      if (!nonEmpty(data.title)) e.title = 'Issue title is required';
      if (!nonEmpty(data.description)) e.description = 'Please describe the issue';
    }
    if (stepIndex === 3) {
      if (!nonEmpty(data.name)) e.name = 'Name is required';
      if (!isEmail(data.email)) e.email = 'Enter a valid email';
      if (!isPhone(data.phone)) e.phone = 'Enter a valid phone number';
      if (!data.consent) e.consent = 'Consent is required to proceed';
    }
    return e;
  };

  // Validator used only on actions (updates state)
  const validate = (stepIndex) => {
    const e = collectErrors(stepIndex);
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // derive disabled state without setting state
  const isNextDisabled = Object.keys(collectErrors(activeStep)).length > 0;

  const onChange = (field, value) => setData((d) => ({ ...d, [field]: value }));

  // AUTOSAVE
  useEffect(() => {
    const saved = localStorage.getItem('maint_req_v2');
    if (saved) {
      try {
        setData((d) => ({ ...d, ...JSON.parse(saved) }));
      } catch {}
    }
  }, []);
  useEffect(() => { localStorage.setItem('maint_req_v2', JSON.stringify(data)); }, [data]);

  const handleNext = () => {
    if (!validate(activeStep)) return;
    setActiveStep((s) => Math.min(s + 1, steps.length - 1));
  };
  const handleBack = () => setActiveStep((s) => Math.max(s - 1, 0));

  const handleSubmit = () => {
    // validate all steps before submitting
    for (let i = 0; i < steps.length - 1; i++) {
      if (!validate(i)) { setActiveStep(i); return; }
    }
    // TODO: replace with real API call
    console.log('Submitting:', {
      ...data,
      phone: data.phone.replace(/\s+/g, '')
    });
    localStorage.removeItem('maint_req_v2');
    setIsSubmitted(true);
  };

  if (isSubmitted) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'grey.100', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 4 }}>
        <Card sx={{ maxWidth: 560, width: '100%', textAlign: 'center', boxShadow: 6, borderRadius: 4 }}>
          <CardHeader
            title="Request Submitted!"
            titleTypographyProps={{ variant: 'h4', fontWeight: 'bold' }}
            subheader="Thank you. We’ll be in touch shortly."
            avatar={<CheckCircleOutlineIcon sx={{ width: 56, height: 56, color: 'success.main' }} />}
            sx={{ pt: 4 }}
          />
          <CardContent>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              We received your request for <strong>{data.address}</strong>.
            </Typography>
            <Alert severity="success" sx={{ textAlign: 'left' }}>
              <Typography variant="subtitle2">What happens next?</Typography>
              <ul style={{ margin: 0, paddingLeft: 16 }}>
                <li>We match the right engineer based on your category and urgency.</li>
                <li>You’ll receive confirmation via {data.email ? 'email' : 'your phone'}.</li>
              </ul>
            </Alert>
          </CardContent>
          <Box sx={{ pb: 3 }}>
            <Button variant="outlined" onClick={() => window.location.reload()}>Create another request</Button>
          </Box>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{
      bgcolor: 'grey.100', minHeight: '100vh', display: 'flex', flexDirection: 'column',
      backgroundImage: 'linear-gradient(180deg, rgba(33,150,243,0.08), rgba(33,150,243,0) 40%)',
    }}>
      <Container maxWidth="lg" sx={{ flexGrow: 1, py: { xs: 3, md: 6 } }}>
        <Card sx={{ width: '100%', display: 'flex', flexDirection: { xs: 'column', md: 'row' }, boxShadow: 6, borderRadius: 4, overflow: 'hidden' }}>
          {/* Left rail */}
          <Box sx={{
            display: { xs: 'none', md: 'flex' }, flexDirection: 'column', justifyContent: 'space-between',
            width: 320, p: 4,
            background: 'linear-gradient(180deg, rgba(25,118,210,0.85) 0%, rgba(25,118,210,0.7) 100%), url(https://images.unsplash.com/photo-1501183638710-841dd1904471?q=80&w=1600&auto=format&fit=crop) center/cover',
            color: 'common.white',
          }}>
            <Box>
              <Typography variant="h5" fontWeight={800}>Maintenance Request</Typography>
              <Typography variant="body2" sx={{ mt: 1, opacity: 0.95 }}>
                Type your location freely; we’ll handle the rest.
              </Typography>
              <Stepper activeStep={activeStep} orientation="vertical" sx={{ mt: 4 }}>
                {steps.map((s) => (
                  <Step key={s.title}>
                    <StepLabel StepIconComponent={StepIconComponent}>
                      <Typography variant="subtitle2" sx={{ color: 'inherit' }}>{s.title}</Typography>
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Box>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>Tip: Add landmarks or access notes for accuracy.</Typography>
          </Box>

          {/* Right – form */}
          <Box sx={{ flexGrow: 1, p: { xs: 3, md: 5 }, bgcolor: 'background.paper' }}>
            {isMobile && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" align="center" fontWeight="bold">{steps[activeStep].title}</Typography>
                <Stepper activeStep={activeStep} alternativeLabel sx={{ mt: 2 }}>
                  {steps.map((s) => (
                    <Step key={s.title}><StepLabel StepIconComponent={StepIconComponent}>{s.title}</StepLabel></Step>
                  ))}
                </Stepper>
              </Box>
            )}

            <Box sx={{ mb: 3 }}>
              <Typography variant="h4" fontWeight="bold" sx={{ display: { xs: 'none', md: 'block' } }}>
                {steps[activeStep].title}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mt: 1, display: { xs: 'none', md: 'block' } }}>
                {activeStep === 0 && 'Type your address/location. City and postcode help us schedule the right team.'}
                {activeStep === 1 && 'Pick the closest category so we route the correct engineer.'}
                {activeStep === 2 && 'Describe the issue, set urgency, and add photos (optional).'}
                {activeStep === 3 && 'Your contact details so we can confirm the visit.'}
                {activeStep === 4 && 'One last look before submitting.'}
              </Typography>
            </Box>

            <Current
              data={data}
              onChange={onChange}
              onPhotosChange={(urls) => setData((d) => ({ ...d, photos: urls }))}
              errors={errors}
            />

            {/* Inline error list when on details step and errors present */}
            {activeStep === 3 && Object.keys(errors).length > 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {errors.name && <div>• {errors.name}</div>}
                {errors.email && <div>• {errors.email}</div>}
                {errors.phone && <div>• {errors.phone}</div>}
                {errors.consent && <div>• {errors.consent}</div>}
              </Alert>
            )}

            {/* Actions */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
              <Button
                disabled={activeStep === 0}
                onClick={handleBack}
                startIcon={<ChevronLeftIcon />}
                variant="outlined"
                sx={{ minWidth: 120 }}
              >
                Back
              </Button>

              {activeStep < steps.length - 1 ? (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  endIcon={<ChevronRightIcon />}
                  disabled={isNextDisabled}
                  sx={{ minWidth: 140, boxShadow: 3, '&:active': { transform: 'translateY(1px)' } }}
                >
                  Next
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  startIcon={<CheckCircleOutlineIcon />}
                  sx={{ minWidth: 180, boxShadow: 3, '&:active': { transform: 'translateY(1px)' } }}
                >
                  Submit Request
                </Button>
              )}
            </Box>
          </Box>
        </Card>
      </Container>
    </Box>
  );
}

export default App;
